import { NotificationReceiver, TwoFactorAuthenticationReceiver } from 'domains/User';
import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { isTaiwanPhone } from 'domains/PhoneNumber';

// スキーマのエラーメッセージは外部から注入する
type EditUserFormSchemaMessages = {
  RequiredMessage: string;
  MailMessage: string;
  PhoneNumberMessage: string;
  KanaMessage: string;
  ConsentRequiredMessage: string;
  MailRequiredMessage: string;
  MaxLengthMessage: (max: number) => string;
  FullWidthMessage: string;
};

// 全角カタカナの正規表現
const ZennkakuKanaRegex = /^[ァ-ヴー]+$/;

const NotificationReceiverSchema = (isGlobal: boolean, phoneNumber: string) => {
  if (isTaiwanPhone(phoneNumber) || !isGlobal) {
    return z.enum([
      NotificationReceiver.SMS,
      NotificationReceiver.MailAddress,
      NotificationReceiver.LINE,
    ]);
  }
  return z.enum([
    NotificationReceiver.WhatsApp,
    NotificationReceiver.MailAddress,
    NotificationReceiver.LINE,
  ]);
};
const TwoFactorAuthenticationReceiverSchema = (isGlobal: boolean, phoneNumber: string) => {
  if (isTaiwanPhone(phoneNumber) || !isGlobal) {
    return z.enum([
      TwoFactorAuthenticationReceiver.SMS,
      TwoFactorAuthenticationReceiver.MailAddress,
    ]);
  }
  return z.enum([
    TwoFactorAuthenticationReceiver.MailAddress,
    TwoFactorAuthenticationReceiver.WhatsApp,
  ]);
};

export const EditUserFormSchema = (
  messages: EditUserFormSchemaMessages,
  phoneNumber: string,
  isGlobal: boolean,
  currentLang: string
) =>
  z
    .object({
      mailAddress: z
        .string()
        .max(100, messages.MaxLengthMessage(100))
        .email(messages.MailMessage)
        .or(z.literal('')),
      phoneNumber: z
        .string({
          required_error: messages.RequiredMessage,
          invalid_type_error: messages.FullWidthMessage,
        })
        .min(1, messages.RequiredMessage)
        .refine((value) => /^[0-9+]+$/.test(value), {
          message: messages.FullWidthMessage,
        })
        .refine(isValidPhoneNumber, {
          message: messages.PhoneNumberMessage,
        }),
      notificationReceiver: NotificationReceiverSchema(isGlobal, phoneNumber),
      twoFactorAuthReceiver: TwoFactorAuthenticationReceiverSchema(isGlobal, phoneNumber),
      firstName: z
        .string()
        .min(1, messages.RequiredMessage)
        .max(100, messages.MaxLengthMessage(100)),
      lastName: z
        .string()
        .min(1, messages.RequiredMessage)
        .max(100, messages.MaxLengthMessage(100)),
      firstNameKana:
        currentLang === 'ja'
          ? z
              .string()
              .min(1, messages.RequiredMessage)
              .regex(ZennkakuKanaRegex, messages.KanaMessage)
              .max(100, messages.MaxLengthMessage(100))
          : z.string().max(100, messages.MaxLengthMessage(100)),
      lastNameKana:
        currentLang === 'ja'
          ? z
              .string()
              .min(1, messages.RequiredMessage)
              .regex(ZennkakuKanaRegex, messages.KanaMessage)
              .max(100, messages.MaxLengthMessage(100))
          : z.string().max(100, messages.MaxLengthMessage(100)),
      birthday: z.date(),
    })
    .superRefine((data, ctx) => {
      // 台湾の電話番号の場合、メールアドレスは必須
      if (isTaiwanPhone(phoneNumber) && !data.mailAddress) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: messages.RequiredMessage,
          path: ['mailAddress'],
        });
      }

      // メール通知または2要素認証でメールを選択している場合、メールアドレスは必須
      const isMailSelected =
        data.notificationReceiver === NotificationReceiver.MailAddress ||
        data.twoFactorAuthReceiver === TwoFactorAuthenticationReceiver.MailAddress;

      if (isMailSelected && !data.mailAddress) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: messages.MailRequiredMessage,
          path: ['mailAddress'],
        });
      }
    });
export type EditUserFormSchemaType = z.infer<ReturnType<typeof EditUserFormSchema>>;
